import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Chapter, Novel, NovelMetadata } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import './AIWriterPage.css';

interface AIWriterPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type WritingMode = 'append' | 'rewrite-all' | 'rewrite-selection' | 'image-generator';

const AIWriterPage: React.FC<AIWriterPageProps> = ({ user, onToggleSidebar }) => {
  const { novelId, chapterId } = useParams<{ novelId: string; chapterId: string }>();
  const navigate = useNavigate();
  
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [allChapters, setAllChapters] = useState<Chapter[]>([]);
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Writing mode
  const [mode, setMode] = useState<WritingMode>('append');
  
  // Selection state
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [selectedText, setSelectedText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // AI configuration
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<'claude' | 'gemini' | 'openai' | 'grok' | null>(null);
  const [selectedChapterIds, setSelectedChapterIds] = useState<string[]>([]);
  const [maxTokens, setMaxTokens] = useState<number>(8000);
  
  // Metadata context
  const [selectedMetadataFields, setSelectedMetadataFields] = useState<string[]>([]);
  const [includeChapterNamesOnly, setIncludeChapterNamesOnly] = useState(false);
  const [includeAllChapterNames, setIncludeAllChapterNames] = useState(false);
  
  // AI generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  
  // UI state
  const [isConfigDrawerOpen, setIsConfigDrawerOpen] = useState(false);
  
  // Preview tab state
  const [activePreviewTab, setActivePreviewTab] = useState<'generated' | 'original'>('generated');
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    instructions: true,
    metadata: false,
    chapterOverview: false,
    specificChapters: false,
    advanced: false
  });
  
  // Image generation state
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Metadata info modal state
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  useEffect(() => {
    if (novelId && chapterId) {
      loadChapter();
    }
  }, [novelId, chapterId]);

  const loadChapter = async () => {
    if (!novelId || !chapterId) return;

    try {
      setLoading(true);
      setError(null);
      
      // Load current chapter
      const data = await NovelService.getChapter(novelId, chapterId);
      setChapter(data);
      
      // Load all chapters and novel metadata for context selection
      const novelData = await NovelService.getNovel(novelId);
      setNovel(novelData);
      const previousChapters = novelData.chapters
        .filter(ch => ch.order < data.order)
        .sort((a, b) => a.order - b.order);
      setAllChapters(previousChapters);
    } catch (err) {
      console.error('Error loading chapter:', err);
      setError('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/novel/${novelId}/chapter/${chapterId}`);
  };

  const handleModeChange = (newMode: WritingMode) => {
    setMode(newMode);
    setShowPreview(false);
    setGeneratedContent('');
    setSelectedText('');
    setSelectionStart(0);
    setSelectionEnd(0);
    setGeneratedImageUrl(null);
    setImagePrompt('');
    setActivePreviewTab('generated');
  };

  const handleTextSelect = () => {
    if (!textareaRef.current) return;
    
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = textareaRef.current.value.substring(start, end);
    
    setSelectionStart(start);
    setSelectionEnd(end);
    setSelectedText(text);
  };

  const handleChapterToggle = (chapterId: string) => {
    setSelectedChapterIds(prev => 
      prev.includes(chapterId)
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId]
    );
  };

  const handleSelectAllChapters = () => {
    if (selectedChapterIds.length === allChapters.length) {
      setSelectedChapterIds([]);
    } else {
      setSelectedChapterIds(allChapters.map(ch => ch.chapter_id));
    }
  };

  const handleMetadataFieldToggle = (field: string) => {
    setSelectedMetadataFields(prev => 
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getMetadataFieldLabel = (field: string): string => {
    const metadata = novel?.metadata;
    switch (field) {
      case 'genre':
        return novel?.genre ? `Genre (${novel.genre})` : 'Genre';
      case 'plots':
        return metadata?.plots?.length ? `Plots (${metadata.plots.length})` : 'Plots';
      case 'characters':
        return metadata?.characters?.length ? `Characters (${metadata.characters.length})` : 'Characters';
      case 'settings':
        return metadata?.settings?.length ? `Settings (${metadata.settings.length})` : 'Settings';
      case 'themes':
        return metadata?.themes?.length ? `Themes (${metadata.themes.length})` : 'Themes';
      case 'summary':
        return 'Summary';
      case 'writingStyle':
        return 'Writing Style';
      case 'targetAudience':
        return 'Target Audience';
      case 'notes':
        return 'Notes';
      default:
        return field;
    }
  };

  const hasMetadataField = (field: string): boolean => {
    const metadata = novel?.metadata;
    switch (field) {
      case 'genre':
        return !!novel?.genre;
      case 'plots':
        return !!(metadata?.plots && metadata.plots.length > 0);
      case 'characters':
        return !!(metadata?.characters && metadata.characters.length > 0);
      case 'settings':
        return !!(metadata?.settings && metadata.settings.length > 0);
      case 'themes':
        return !!(metadata?.themes && metadata.themes.length > 0);
      case 'summary':
        return !!(metadata?.oneParagraphSummary && metadata.oneParagraphSummary.trim());
      case 'writingStyle':
        return !!(metadata?.writingStyle && metadata.writingStyle.trim());
      case 'targetAudience':
        return !!(metadata?.targetAudience && metadata.targetAudience.trim());
      case 'notes':
        return !!(metadata?.notes && metadata.notes.trim());
      default:
        return false;
    }
  };

  const hasAnyMetadata = (): boolean => {
    return ['genre', 'plots', 'characters', 'settings', 'themes', 'summary', 'writingStyle', 'targetAudience', 'notes']
      .some(field => hasMetadataField(field));
  };

  const handleGenerate = async () => {
    if (!selectedProvider || !chapter) return;

    try {
      setIsGenerating(true);
      setError(null);

      const chapterContent = chapter.content?.text_value || '';

      // Build selected metadata from novel metadata
      const selectedMetadata: Partial<NovelMetadata> = {};
      if (novel?.metadata && selectedMetadataFields.length > 0) {
        selectedMetadataFields.forEach(field => {
          if (field === 'plots' && novel.metadata?.plots) {
            selectedMetadata.plots = novel.metadata.plots;
          } else if (field === 'characters' && novel.metadata?.characters) {
            selectedMetadata.characters = novel.metadata.characters;
          } else if (field === 'settings' && novel.metadata?.settings) {
            selectedMetadata.settings = novel.metadata.settings;
          } else if (field === 'themes' && novel.metadata?.themes) {
            selectedMetadata.themes = novel.metadata.themes;
          } else if (field === 'summary' && novel.metadata?.oneParagraphSummary) {
            selectedMetadata.oneParagraphSummary = novel.metadata.oneParagraphSummary;
          } else if (field === 'writingStyle' && novel.metadata?.writingStyle) {
            selectedMetadata.writingStyle = novel.metadata.writingStyle;
          } else if (field === 'targetAudience' && novel.metadata?.targetAudience) {
            selectedMetadata.targetAudience = novel.metadata.targetAudience;
          } else if (field === 'notes' && novel.metadata?.notes) {
            selectedMetadata.notes = novel.metadata.notes;
          }
        });
      }

      // Get selected previous chapters context (titles or full content)
      const previousChaptersContext = selectedChapterIds.length > 0
        ? includeChapterNamesOnly
          ? allChapters
              .filter(ch => selectedChapterIds.includes(ch.chapter_id))
              .sort((a, b) => a.order - b.order)
              .map(ch => ch.title)
          : await Promise.all(
              selectedChapterIds.map(async (id) => {
                const ch = allChapters.find(c => c.chapter_id === id);
                if (!ch) return null;
                
                const fullChapter = ch.content 
                  ? ch 
                  : await NovelService.getChapter(novelId!, id);
                
                return {
                  title: fullChapter.title,
                  content: fullChapter.content?.text_value || ''
                };
              })
            ).then(chapters => chapters.filter(ch => ch !== null) as { title: string; content: string }[])
        : undefined;

      // Build all chapter names list if option is enabled
      const allChapterNamesList = includeAllChapterNames && novel?.chapters
        ? novel.chapters
            .sort((a, b) => a.order - b.order)
            .map(ch => ch.title)
        : undefined;

      let generated = '';

      if (mode === 'append') {
        generated = await NovelService.continueChapter(
          selectedProvider,
          chapterContent,
          customPrompt,
          previousChaptersContext,
          chapter.title,
          maxTokens,
          selectedMetadataFields.includes('genre') ? novel?.genre : undefined,
          selectedMetadata,
          allChapterNamesList
        );
      } else if (mode === 'rewrite-all') {
        generated = await NovelService.rewriteChapter(
          selectedProvider,
          chapterContent,
          customPrompt,
          previousChaptersContext,
          chapter.title,
          maxTokens,
          selectedMetadataFields.includes('genre') ? novel?.genre : undefined,
          selectedMetadata,
          allChapterNamesList
        );
      } else if (mode === 'rewrite-selection') {
        if (!selectedText) {
          setError('Please select text to rewrite');
          return;
        }

        const contextBefore = chapterContent.substring(Math.max(0, selectionStart - 200), selectionStart);
        const contextAfter = chapterContent.substring(selectionEnd, Math.min(chapterContent.length, selectionEnd + 200));

        generated = await NovelService.rewriteSelection(
          selectedProvider,
          selectedText,
          customPrompt || 'Rewrite this text to improve clarity and flow',
          contextBefore,
          contextAfter,
          maxTokens,
          selectedMetadataFields.includes('genre') ? novel?.genre : undefined,
          selectedMetadata,
          allChapterNamesList
        );
      }

      setGeneratedContent(generated);
      setShowPreview(true);
      setIsConfigDrawerOpen(false); // Close drawer after generating
    } catch (err) {
      console.error('Error generating content:', err);
      setError('Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async (provider: 'claude' | 'gemini' | 'openai' | 'grok') => {
    setSelectedProvider(provider);
    // Wait for state to update, then generate
    setTimeout(() => handleGenerate(), 0);
  };

  const handleAccept = async () => {
    if (!chapter || !novelId) return;

    try {
      setIsSaving(true);
      
      let newContent = '';
      const currentContent = chapter.content?.text_value || '';

      if (mode === 'append') {
        newContent = currentContent && currentContent.trim()
          ? currentContent + '\n\n' + generatedContent
          : generatedContent;
      } else if (mode === 'rewrite-all') {
        newContent = generatedContent;
      } else if (mode === 'rewrite-selection') {
        newContent = currentContent.substring(0, selectionStart) + 
                    generatedContent + 
                    currentContent.substring(selectionEnd);
      }

      await NovelService.updateChapter(novelId, chapter.chapter_id, {
        content: newContent
      });

      // Navigate back to chapter editor
      navigate(`/novel/${novelId}/chapter/${chapterId}`);
    } catch (err) {
      console.error('Error saving content:', err);
      setError('Failed to save content');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = () => {
    setShowPreview(false);
    setGeneratedContent('');
    setActivePreviewTab('generated');
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt || !novelId || !chapterId) return;

    try {
      setIsGeneratingImage(true);
      setError(null);

      const imageUrl = await NovelService.generateAndUploadImage(
        novelId,
        chapterId,
        imagePrompt,
        selectedText
      );

      setGeneratedImageUrl(imageUrl);
      setIsConfigDrawerOpen(false); // Close drawer after generating image
    } catch (err) {
      console.error('Error generating image:', err);
      setError('Failed to generate image');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleInsertImage = async () => {
    if (!generatedImageUrl || !chapter || !novelId) return;

    try {
      setIsSaving(true);
      
      const currentContent = chapter.content?.text_value || '';
      const imageAlt = imagePrompt.substring(0, 100); // Use first 100 chars as alt text
      const imageMarkdown = `![${imageAlt}](${generatedImageUrl})`;
      
      let newContent = '';
      if (selectedText && selectionEnd > 0) {
        // Insert at selection point
        newContent = currentContent.substring(0, selectionEnd) + 
                    '\n\n' + imageMarkdown + '\n\n' + 
                    currentContent.substring(selectionEnd);
      } else {
        // Insert at beginning if no selection
        newContent = imageMarkdown + '\n\n' + currentContent;
      }

      await NovelService.updateChapter(novelId, chapter.chapter_id, {
        content: newContent
      });

      // Reset image generation state
      setGeneratedImageUrl(null);
      setImagePrompt('');
      setSelectedText('');
      
      // Reload chapter to show updated content
      await loadChapter();
      
      setError(null);
    } catch (err) {
      console.error('Error inserting image:', err);
      setError('Failed to insert image');
    } finally {
      setIsSaving(false);
    }
  };

  const getPreviewOriginalContent = () => {
    if (!chapter) return '';
    const content = chapter.content?.text_value || '';
    
    if (mode === 'append') {
      return content;
    } else if (mode === 'rewrite-all') {
      return content;
    } else if (mode === 'rewrite-selection') {
      return selectedText;
    }
    return content;
  };

  const getPreviewTitle = () => {
    if (mode === 'append') return 'Current Chapter';
    if (mode === 'rewrite-all') return 'Original Chapter';
    if (mode === 'rewrite-selection') return 'Original Selection';
    return 'Original';
  };

  if (loading) {
    return (
      <div className="ai-writer-page-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">AI Writer</h1>}
          rightActions={
            <button className="ai-writer-back-btn" onClick={handleBack}>
              ← Back
            </button>
          }
        />
        <div className="ai-writer-loading">Loading chapter...</div>
      </div>
    );
  }

  if (error && !chapter) {
    return (
      <div className="ai-writer-page-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">AI Writer</h1>}
          rightActions={
            <button className="ai-writer-back-btn" onClick={handleBack}>
              ← Back
            </button>
          }
        />
        <div className="ai-writer-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="ai-writer-page-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">AI Writer - {chapter?.title}</h1>}
        leftActions={
          <button className="ai-writer-back-btn" onClick={handleBack}>
            ← Back
          </button>
        }
        rightActions={
          showPreview && (
            <button 
              className="ai-writer-save-btn"
              onClick={handleAccept}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save & Return'}
            </button>
          )
        }
      />

      {error && (
        <div className="ai-writer-error-banner">
          {error}
          <button 
            className="ai-writer-error-close"
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="ai-writer-content">
        {!showPreview ? (
          <>
            {/* Mode Selector */}
            <div className="ai-writer-mode-selector">
              <button
                className={`ai-writer-mode-btn ${mode === 'append' ? 'active' : ''}`}
                onClick={() => handleModeChange('append')}
              >
                Append
              </button>
              <button
                className={`ai-writer-mode-btn ${mode === 'rewrite-all' ? 'active' : ''}`}
                onClick={() => handleModeChange('rewrite-all')}
              >
                Rewrite All
              </button>
              <button
                className={`ai-writer-mode-btn ${mode === 'rewrite-selection' ? 'active' : ''}`}
                onClick={() => handleModeChange('rewrite-selection')}
              >
                Rewrite Selection
              </button>
              <button
                className={`ai-writer-mode-btn ${mode === 'image-generator' ? 'active' : ''}`}
                onClick={() => handleModeChange('image-generator' as WritingMode)}
              >
                Image Generator
              </button>
            </div>

            {/* Full-width Content Panel */}
            <div className="ai-writer-full-content-area">
              <div className="ai-writer-content-panel">
                <h3 className="ai-writer-panel-title">
                  {mode === 'append' && 'Current Chapter'}
                  {mode === 'rewrite-all' && 'Chapter to Rewrite'}
                  {mode === 'rewrite-selection' && 'Select Text to Rewrite'}
                  {mode === 'image-generator' && 'Chapter Content (Optional: Select Text for Context)'}
                </h3>

                {mode === 'rewrite-selection' || mode === 'image-generator' ? (
                  <>
                    <textarea
                      ref={textareaRef}
                      className="ai-writer-content-textarea"
                      value={chapter?.content?.text_value || ''}
                      onSelect={handleTextSelect}
                      onKeyDown={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                    />
                    {selectedText && mode === 'rewrite-selection' && (
                      <div className="ai-writer-selection-info">
                        <strong>Selected:</strong> {selectedText.length} characters
                        <div className="ai-writer-selection-preview">
                          {selectedText.substring(0, 100)}
                          {selectedText.length > 100 ? '...' : ''}
                        </div>
                      </div>
                    )}
                    {selectedText && mode === 'image-generator' && (
                      <div className="ai-writer-selection-info">
                        <strong>Selected text available</strong> - You can use it as image prompt
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ai-writer-content-display">
                    {chapter?.content?.text_value || 'No content yet'}
                    {mode === 'append' && (
                      <div className="ai-writer-append-indicator">
                        <div className="ai-writer-append-line" />
                        <span className="ai-writer-append-text">AI content will be appended here</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Image Preview Area (for image generator mode) */}
              {mode === 'image-generator' && (
                <>
                  {isGeneratingImage && (
                    <div className="ai-writer-image-loading">
                      <div className="ai-writer-loading-spinner"></div>
                      <p>Generating image, please wait...</p>
                    </div>
                  )}

                  {generatedImageUrl && !isGeneratingImage && (
                    <div className="ai-writer-image-preview-section">
                      <h3 className="ai-writer-panel-title">Generated Image</h3>
                      <img src={generatedImageUrl} alt="Generated" className="ai-writer-generated-image" />
                      <button
                        className="ai-writer-insert-image-btn"
                        onClick={handleInsertImage}
                        disabled={isSaving}
                      >
                        {isSaving ? 'Inserting...' : selectedText ? 'Insert at Selection' : 'Insert at Beginning'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Floating Action Button */}
            <button 
              className="ai-writer-config-fab"
              onClick={() => setIsConfigDrawerOpen(true)}
              title="Configure AI generation"
            >
              ⚙️ Configure
            </button>

            {/* Configuration Drawer */}
            {isConfigDrawerOpen && (
              <div 
                className="ai-writer-config-drawer-overlay"
                onClick={() => setIsConfigDrawerOpen(false)}
              >
                <div 
                  className="ai-writer-config-drawer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Drawer Header */}
                  <div className="ai-writer-drawer-header">
                    <h3 className="ai-writer-drawer-title">Configuration</h3>
                    <button 
                      className="ai-writer-drawer-close"
                      onClick={() => setIsConfigDrawerOpen(false)}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Drawer Body - Scrollable */}
                  <div className="ai-writer-drawer-body">
                    {/* Custom Instructions */}
                    <div className="ai-writer-collapsible-section">
                      <button 
                        className="ai-writer-section-toggle"
                        onClick={() => toggleSection('instructions')}
                      >
                        <span className="ai-writer-section-toggle-text">
                          /instructions {mode === 'rewrite-selection' && !customPrompt ? '(required)' : '(optional)'}
                        </span>
                        <span className="ai-writer-section-toggle-arrow">
                          {expandedSections.instructions ? '▼' : '▶'}
                        </span>
                      </button>
                      {expandedSections.instructions && (
                        <div className="ai-writer-section-content">
                          <textarea
                            className="ai-writer-textarea"
                            placeholder={
                              mode === 'append' 
                                ? 'e.g., Add more suspense, focus on character emotions...'
                                : mode === 'rewrite-all'
                                ? 'e.g., Make it more dramatic, simplify the language...'
                                : mode === 'image-generator'
                                ? 'Describe the image you want to generate...'
                                : 'e.g., Make it more concise, improve clarity...'
                            }
                            value={mode === 'image-generator' ? imagePrompt : customPrompt}
                            onChange={(e) => mode === 'image-generator' ? setImagePrompt(e.target.value) : setCustomPrompt(e.target.value)}
                            rows={4}
                          />
                          {mode === 'image-generator' && selectedText && (
                            <button
                              className="ai-writer-fill-prompt-btn"
                              onClick={() => setImagePrompt(selectedText.substring(0, 500))}
                            >
                              Use Selected Text as Prompt
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Metadata Context - only for non-image modes */}
                    {mode !== 'image-generator' && hasAnyMetadata() && (
                      <div className="ai-writer-collapsible-section">
                        <button 
                          className="ai-writer-section-toggle"
                          onClick={() => toggleSection('metadata')}
                        >
                          <div className="ai-writer-section-toggle-header">
                            <span className="ai-writer-section-toggle-text">
                              /metadata {selectedMetadataFields.length > 0 ? `(${selectedMetadataFields.length})` : ''}
                            </span>
                            <button
                              className="ai-writer-metadata-info-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowMetadataModal(true);
                              }}
                              title="View metadata details"
                            >
                              ℹ️
                            </button>
                          </div>
                          <span className="ai-writer-section-toggle-arrow">
                            {expandedSections.metadata ? '▼' : '▶'}
                          </span>
                        </button>
                        {expandedSections.metadata && (
                          <div className="ai-writer-section-content">
                            <div className="ai-writer-metadata-section">
                              {['genre', 'plots', 'characters', 'settings', 'themes', 'summary', 'writingStyle', 'targetAudience', 'notes'].map(field => (
                                hasMetadataField(field) && (
                                  <label key={field} className="ai-writer-metadata-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={selectedMetadataFields.includes(field)}
                                      onChange={() => handleMetadataFieldToggle(field)}
                                    />
                                    <span className="ai-writer-metadata-field-label">
                                      {getMetadataFieldLabel(field)}
                                    </span>
                                  </label>
                                )
                              ))}
                            </div>
                            {selectedMetadataFields.length > 0 && (
                              <div className="ai-writer-info">
                                {selectedMetadataFields.length} field{selectedMetadataFields.length > 1 ? 's' : ''} selected
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Chapter Overview - only for non-image modes */}
                    {mode !== 'image-generator' && novel?.chapters && novel.chapters.length > 0 && (
                      <div className="ai-writer-collapsible-section">
                        <button 
                          className="ai-writer-section-toggle"
                          onClick={() => toggleSection('chapterOverview')}
                        >
                          <span className="ai-writer-section-toggle-text">
                            /chapter-overview {includeAllChapterNames ? '(enabled)' : ''}
                          </span>
                          <span className="ai-writer-section-toggle-arrow">
                            {expandedSections.chapterOverview ? '▼' : '▶'}
                          </span>
                        </button>
                        {expandedSections.chapterOverview && (
                          <div className="ai-writer-section-content">
                            <label className="ai-writer-chapter-overview-toggle">
                              <input
                                type="checkbox"
                                checked={includeAllChapterNames}
                                onChange={(e) => setIncludeAllChapterNames(e.target.checked)}
                              />
                              <span>Include all chapter names for story structure</span>
                            </label>
                            {includeAllChapterNames && (
                              <div className="ai-writer-info">
                                {novel.chapters.length} chapter{novel.chapters.length > 1 ? 's' : ''} included
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Specific Chapters - only for append/rewrite-all modes */}
                    {mode !== 'rewrite-selection' && mode !== 'image-generator' && allChapters.length > 0 && (
                      <div className="ai-writer-collapsible-section">
                        <button 
                          className="ai-writer-section-toggle"
                          onClick={() => toggleSection('specificChapters')}
                        >
                          <span className="ai-writer-section-toggle-text">
                            /previous-chapters {selectedChapterIds.length > 0 ? `(${selectedChapterIds.length})` : ''}
                          </span>
                          <span className="ai-writer-section-toggle-arrow">
                            {expandedSections.specificChapters ? '▼' : '▶'}
                          </span>
                        </button>
                        {expandedSections.specificChapters && (
                          <div className="ai-writer-section-content">
                            <div className="ai-writer-label-row">
                              <span className="ai-writer-section-label">Select chapters for context</span>
                              {allChapters.length > 1 && (
                                <button 
                                  className="ai-writer-select-all"
                                  onClick={handleSelectAllChapters}
                                >
                                  {selectedChapterIds.length === allChapters.length ? 'Deselect All' : 'Select All'}
                                </button>
                              )}
                            </div>
                            {selectedChapterIds.length > 0 && (
                              <label className="ai-writer-chapter-mode-toggle">
                                <input
                                  type="checkbox"
                                  checked={includeChapterNamesOnly}
                                  onChange={(e) => setIncludeChapterNamesOnly(e.target.checked)}
                                />
                                <span>Include names only (faster, less tokens)</span>
                              </label>
                            )}
                            <div className="ai-writer-chapters">
                              {allChapters.map(ch => (
                                <label key={ch.chapter_id} className="ai-writer-chapter-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedChapterIds.includes(ch.chapter_id)}
                                    onChange={() => handleChapterToggle(ch.chapter_id)}
                                  />
                                  <span className="ai-writer-chapter-title">{ch.title}</span>
                                </label>
                              ))}
                            </div>
                            {selectedChapterIds.length > 0 && (
                              <div className="ai-writer-info">
                                {includeChapterNamesOnly
                                  ? `${selectedChapterIds.length} name${selectedChapterIds.length > 1 ? 's' : ''} selected`
                                  : `${selectedChapterIds.length} chapter${selectedChapterIds.length > 1 ? 's' : ''} selected`
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Max Tokens - only for non-image modes */}
                    {mode !== 'image-generator' && (
                      <div className="ai-writer-collapsible-section">
                        <button 
                          className="ai-writer-section-toggle"
                          onClick={() => toggleSection('advanced')}
                        >
                          <span className="ai-writer-section-toggle-text">
                            /advanced {maxTokens && maxTokens !== 8000 ? `(${maxTokens})` : ''}
                          </span>
                          <span className="ai-writer-section-toggle-arrow">
                            {expandedSections.advanced ? '▼' : '▶'}
                          </span>
                        </button>
                        {expandedSections.advanced && (
                          <div className="ai-writer-section-content">
                            <label className="ai-writer-section-label">Max Tokens</label>
                            <input
                              type="number"
                              className="ai-writer-input"
                              placeholder="e.g., 8000"
                              value={maxTokens}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setMaxTokens(0);
                                } else {
                                  const numValue = parseInt(value);
                                  if (!isNaN(numValue)) {
                                    setMaxTokens(Math.max(1, Math.min(100000, numValue)));
                                  }
                                }
                              }}
                              min="1"
                              max="100000"
                            />
                            <div className="ai-writer-hint">
                              Approximate tokens to generate (1 token ≈ 0.75 words)
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Drawer Footer */}
                  <div className="ai-writer-drawer-footer">
                    {mode === 'image-generator' ? (
                      <button 
                        className="ai-writer-generate-btn"
                        onClick={handleGenerateImage}
                        disabled={!imagePrompt || isGeneratingImage}
                      >
                        {isGeneratingImage ? 'Generating Image...' : 'Generate Image'}
                      </button>
                    ) : (
                      <>
                        <div className="ai-writer-drawer-section">
                          <label className="ai-writer-label">AI Provider</label>
                          <div className="ai-writer-providers">
                            <button 
                              className={`ai-writer-provider-btn ${selectedProvider === 'claude' ? 'selected' : ''}`}
                              onClick={() => setSelectedProvider('claude')}
                            >
                              Claude
                            </button>
                            <button 
                              className={`ai-writer-provider-btn ${selectedProvider === 'gemini' ? 'selected' : ''}`}
                              onClick={() => setSelectedProvider('gemini')}
                            >
                              Gemini
                            </button>
                            <button 
                              className={`ai-writer-provider-btn ${selectedProvider === 'openai' ? 'selected' : ''}`}
                              onClick={() => setSelectedProvider('openai')}
                            >
                              OpenAI
                            </button>
                            <button 
                              className={`ai-writer-provider-btn ${selectedProvider === 'grok' ? 'selected' : ''}`}
                              onClick={() => setSelectedProvider('grok')}
                            >
                              Grok
                            </button>
                          </div>
                        </div>

                        <button 
                          className="ai-writer-generate-btn"
                          onClick={handleGenerate}
                          disabled={!selectedProvider || isGenerating || (mode === 'rewrite-selection' && !selectedText)}
                        >
                          {isGenerating ? 'Generating...' : 'Generate'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Preview Panel */
          <div className="ai-writer-preview-panel">
            <div className="ai-writer-preview-header">
              <h3 className="ai-writer-panel-title">Preview Generated Content</h3>

              {customPrompt && (
                <div className="ai-writer-prompt-display">
                  <span className="ai-writer-prompt-label">Instructions:</span>
                  <span className="ai-writer-prompt-text">{customPrompt}</span>
                </div>
              )}
            </div>

            {/* Tab Selector */}
            <div className="ai-writer-preview-tabs">
              <button
                className={`ai-writer-preview-tab ${activePreviewTab === 'generated' ? 'active' : ''}`}
                onClick={() => setActivePreviewTab('generated')}
              >
                AI Generated
                <span className="ai-writer-preview-badge ai-writer-preview-badge-new">New</span>
              </button>
              <button
                className={`ai-writer-preview-tab ${activePreviewTab === 'original' ? 'active' : ''}`}
                onClick={() => setActivePreviewTab('original')}
              >
                {getPreviewTitle()}
                <span className="ai-writer-preview-badge">Original</span>
              </button>
            </div>

            {/* Tab Content */}
            <div className="ai-writer-preview-content">
              {activePreviewTab === 'generated' ? (
                <div className="ai-writer-preview-tab-panel">
                  <div className="ai-writer-preview-text ai-writer-preview-generated">
                    {generatedContent}
                  </div>
                </div>
              ) : (
                <div className="ai-writer-preview-tab-panel">
                  <div className="ai-writer-preview-text">
                    {getPreviewOriginalContent()}
                  </div>
                </div>
              )}
            </div>

            <div className="ai-writer-preview-actions">
              <div className="ai-writer-preview-actions-left">
                <button 
                  className="ai-writer-preview-btn ai-writer-preview-btn-secondary"
                  onClick={handleReject}
                >
                  Reject
                </button>
              </div>
              <div className="ai-writer-preview-actions-right">
                <div className="ai-writer-regenerate-group">
                  <span className="ai-writer-regenerate-label">Regenerate with:</span>
                  <button
                    className="ai-writer-preview-btn ai-writer-preview-btn-small"
                    onClick={() => handleRegenerate('claude')}
                    disabled={isGenerating}
                  >
                    Claude
                  </button>
                  <button
                    className="ai-writer-preview-btn ai-writer-preview-btn-small"
                    onClick={() => handleRegenerate('gemini')}
                    disabled={isGenerating}
                  >
                    Gemini
                  </button>
                  <button
                    className="ai-writer-preview-btn ai-writer-preview-btn-small"
                    onClick={() => handleRegenerate('openai')}
                    disabled={isGenerating}
                  >
                    OpenAI
                  </button>
                  <button
                    className="ai-writer-preview-btn ai-writer-preview-btn-small"
                    onClick={() => handleRegenerate('grok')}
                    disabled={isGenerating}
                  >
                    Grok
                  </button>
                </div>
                <button 
                  className="ai-writer-preview-btn ai-writer-preview-btn-primary"
                  onClick={handleAccept}
                  disabled={isGenerating || isSaving}
                >
                  {isSaving ? 'Saving...' : 'Accept'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Metadata Info Modal */}
      {showMetadataModal && (
        <div 
          className="ai-writer-modal-overlay"
          onClick={() => setShowMetadataModal(false)}
        >
          <div 
            className="ai-writer-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ai-writer-modal-header">
              <h3 className="ai-writer-modal-title">Novel Metadata</h3>
              <button 
                className="ai-writer-modal-close"
                onClick={() => setShowMetadataModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="ai-writer-modal-body">
              {novel?.genre && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Genre</label>
                  <div className="ai-writer-modal-field-value">{novel.genre}</div>
                </div>
              )}

              {novel?.metadata?.oneParagraphSummary && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Summary</label>
                  <div className="ai-writer-modal-field-value">{novel.metadata.oneParagraphSummary}</div>
                </div>
              )}

              {novel?.metadata?.plots && novel.metadata.plots.length > 0 && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Plots ({novel.metadata.plots.length})</label>
                  <div className="ai-writer-modal-field-list">
                    {novel.metadata.plots.map((plot, index) => (
                      <div key={index} className="ai-writer-modal-list-item">
                        {plot}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {novel?.metadata?.characters && novel.metadata.characters.length > 0 && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Characters ({novel.metadata.characters.length})</label>
                  <div className="ai-writer-modal-field-list">
                    {novel.metadata.characters.map((char, index) => (
                      <div key={index} className="ai-writer-modal-list-item">
                        <strong>{char.name}</strong>
                        {char.role && <span className="ai-writer-modal-char-role"> ({char.role})</span>}
                        {char.description && <p>{char.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {novel?.metadata?.settings && novel.metadata.settings.length > 0 && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Settings ({novel.metadata.settings.length})</label>
                  <div className="ai-writer-modal-field-list">
                    {novel.metadata.settings.map((setting, index) => (
                      <div key={index} className="ai-writer-modal-list-item">
                        {setting}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {novel?.metadata?.themes && novel.metadata.themes.length > 0 && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Themes ({novel.metadata.themes.length})</label>
                  <div className="ai-writer-modal-field-list">
                    {novel.metadata.themes.map((theme, index) => (
                      <div key={index} className="ai-writer-modal-list-item">
                        {theme}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {novel?.metadata?.writingStyle && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Writing Style</label>
                  <div className="ai-writer-modal-field-value">{novel.metadata.writingStyle}</div>
                </div>
              )}

              {novel?.metadata?.targetAudience && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Target Audience</label>
                  <div className="ai-writer-modal-field-value">{novel.metadata.targetAudience}</div>
                </div>
              )}

              {novel?.metadata?.notes && (
                <div className="ai-writer-modal-field">
                  <label className="ai-writer-modal-field-label">Notes</label>
                  <div className="ai-writer-modal-field-value">{novel.metadata.notes}</div>
                </div>
              )}
            </div>

            <div className="ai-writer-modal-footer">
              <a
                href={`/novel/${novelId}/metadata`}
                className="ai-writer-modal-edit-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Edit Metadata →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIWriterPage;

